import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import WarningIcon from "@mui/icons-material/DeleteOutline";
import { red } from "@mui/material/colors";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import MDTypography from "components/MDTypography";
import CTLNotification from "application/components/CTLNotification";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import { useMaterialUIController } from "context";
import { Typography } from "@mui/material"
import {
  setAmbientMode,
  setClickToCall,
  setClickToVideoCall,
  setIsSOSCall,
} from "context";
import { application, telemetry } from "globals/endpoints";
import { getColumns, operationURLS } from "./model";
import MDButton from "components/MDButton";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import MDBox from "components/MDBox";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CTLBatteryStatus from "application/components/CTLBatteryStatus";

const SORT_OPTIONS = [
  { value: "emergencyCreatedOn", label: "Latest", sortDataType: "date" },
  { value: "extensionNumber", label: "Number", sortDataType: "string" },
];

const DEFAULT_PAGE_SIZE = 10;
const EXPANDED_PAGE_SIZE = 24;
const DISPATCHER_OPEN_CHAT_EVENT = "dispatcher-open-chat";

const emptyFallback = (value, fallback = "--") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return value;
};

export default function AlarmList({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  onContactAction,
  isRightPanelExpanded = false,
  isCompactScreen = false,
  isPhoneCallActive = false,
  sosCallList = [],
}) {
  const attributes = {
    enableDownloadButton: false,
    enableAdvanceSearch: true,
    modes: [],
  };

  const [controller, dispatch] = useMaterialUIController();
  const { activePage, darkMode } = controller;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const departmentList = AuthenticationService.getDepartmentList() || [];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("emergencyCreatedOn");
  const [sortDirection, setSortDirection] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState(searchText || "");
  const [refreshTick, setRefreshTick] = useState(0);
  const hasActiveSOS = rows.some((row) => row?.callStatus === "Active");
  const refreshIntervalMs = hasActiveSOS ? 3000 : 5000;
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const [openClearAllSOSDialog, setOpenClearAllSOSDialog] = useState(false);

  const selectedSortOption =
    SORT_OPTIONS.find((option) => option.value === sortBy) || SORT_OPTIONS[0];
  const resolvedSortDataType = selectedSortOption?.sortDataType || "";
  const pageSize =
    isRightPanelExpanded && !isCompactScreen
      ? EXPANDED_PAGE_SIZE
      : DEFAULT_PAGE_SIZE;

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (typeof onLoad === "function") {
      onLoad(attributes);
    }
  }, [onLoad, pageId]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearch((searchText || "").trim());
      setCurrentPage(0);
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortBy, sortDirection, advancedFilters]);

  useEffect(() => {
    hasLoadedRef.current = false;
    setCurrentPage(0);
  }, [pageSize]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setRefreshTick((previous) => previous + 1);
    }, refreshIntervalMs);

    return () => clearInterval(refreshInterval);
  }, [refreshIntervalMs]);

  const fetchEmergencyList = useCallback(
    async ({ page = 0, silent = false } = {}) => {
      const shouldShowLoader = !silent && !hasLoadedRef.current;

      if (shouldShowLoader) {
        setLoading(true);
      }

      try {
        const payload = {
          searchRequest: {
            currentPage: page,
            pageSize,
            sortDirection,
            sortBy,
            search: debouncedSearch,
            sortDataType: resolvedSortDataType,
            advancedFilters,
          },
          departmentList: departmentList?.map((dept) => dept.department),
        };

        const response = await application.post(operationURLS.list, payload);
        const responseData = response?.data?.data || {};
        const pageRows = Array.isArray(responseData.currentPageData)
          ? responseData.currentPageData
          : [];

        setRows(pageRows);
        setCurrentPage(
          typeof responseData.currentPage === "number"
            ? responseData.currentPage
            : page,
        );
        setTotalPages(
          typeof responseData.totalPages === "number"
            ? responseData.totalPages
            : 0,
        );
        setTotalRecords(
          typeof responseData.totalRecords === "number"
            ? responseData.totalRecords
            : pageRows.length,
        );
        hasLoadedRef.current = true;
      } catch (error) {
        if (sortBy !== "emergencyCreatedOn") {
          setNotify({
            isOpen: true,
            message: "",
            type: "warning",
            pagename: "Alarm View",
            status:
              "Selected sort is not supported by server. Switched to Latest.",
          });
          setSortBy("emergencyCreatedOn");
          setSortDirection("desc");
          return;
        }

        setNotify({
          isOpen: true,
          message:
            error?.response?.data?.message || "Emergency list fetch failed",
          type: "error",
          pagename: "Alarm View",
          status:
            error?.response?.data?.messageDetail ||
            error?.message ||
            "Unable to fetch emergency records.",
        });
      } finally {
        if (shouldShowLoader) {
          setLoading(false);
        }
      }
    },
    [
      advancedFilters,
      debouncedSearch,
      pageSize,
      resolvedSortDataType,
      sortBy,
      sortDirection,
    ],
  );

  useEffect(() => {
    fetchEmergencyList({
      page: currentPage,
      silent: hasLoadedRef.current && refreshTick > 0,
    });
  }, [currentPage, fetchEmergencyList, refreshTick]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      setCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [currentPage, totalPages]);

  const onOpenFilter = useCallback(() => {
    setOpenFilter(true);
  }, []);

  const onCloseFilter = useCallback(() => {
    setOpenFilter(false);
  }, []);

  const onApplyFilter = useCallback((filters) => {
    setAdvancedFilters(Array.isArray(filters) ? filters : []);
    setOpenFilter(false);
  }, []);

  const onDownload = useCallback(() => {
    const requestBodyData = {
      sortBy,
      search: debouncedSearch,
      sortDataType: resolvedSortDataType,
      sortDirection,
      columnNames: [
        "emergencyCreatedOn",
        "designation",
        "extensionNumber",
        "department",
        "zone",
      ],
      advancedFilters,
    };
    const requestConfig = {
      responseType: "arraybuffer",
    };

    application
      .post(operationURLS.csvDownload, requestBodyData, requestConfig)
      .then((response) => {
        if (response) {
          const blob = new Blob([response.data], { type: "text/csv" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "Alarm-Log.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);

          setNotify({
            isOpen: true,
            pagename: "Alarm View",
            status: "CSV Download successful",
            type: "success",
          });
        }
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          pagename: "Alarm View",
          status:
            error?.response?.data?.messageDetail || "Unable to download file",
          type: "error",
        });
      });
  }, [
    advancedFilters,
    debouncedSearch,
    resolvedSortDataType,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    if (typeof registerCallBacks === "function") {
      registerCallBacks({
        DownloadData: onDownload,
        openAdvancedFilter: onOpenFilter,
      });
    }
  }, [registerCallBacks, activePage, onDownload, onOpenFilter]);

  const onClickToCall = (data) => {
    const target = data?.extensionNumber;

    if (!target) {
      return;
    }

    setClickToCall(dispatch, target);

    if (window.disablePhone) {
      window?.chrome?.webview?.postMessage(
        JSON.stringify({ eventType: "Call", data: target }),
      );
      return;
    }

    setIsSOSCall(dispatch, true);
    setClickToCall(dispatch, target);
  };

  const onClickToVideoCall = (data) => {
    const target = data?.extensionNumber;

    if (!target) {
      return;
    }

    setClickToVideoCall(dispatch, target);

    if (window.disablePhone) {
      window?.chrome?.webview?.postMessage(
        JSON.stringify({ eventType: "VideoCall", data: target }),
      );
      return;
    }

    setIsSOSCall(dispatch, true);
    setClickToVideoCall(dispatch, target);
  };

  const onClickToAmbientMode = async (data) => {
    const target = data?.extensionNumber;

    if (!target) {
      return;
    }

    try {
      const payload = {
        kind: "D",
        sender: AuthenticationService.getExtensionNumber(),
        target,
        content: "callType: Ambient; callState: RequestAmbient;",
        messageType: "call",
      };

      await telemetry.post("chat/message", payload);
      setAmbientMode(dispatch, true);
      setNotify({
        isOpen: true,
        message: "",
        type: "info",
        pagename: "Alarm View",
        status: `Ambient mode request sent to ${target}`,
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: "Alarm View",
        status:
          error?.response?.data?.messageDetail ||
          "Unable to send ambient mode request.",
      });
    }
  };

  const onClickToMessage = (data) => {
    const target = String(data?.extensionNumber || "").trim();

    if (!target) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(DISPATCHER_OPEN_CHAT_EVENT, {
        detail: {
          offEXtn: target,
          extension: target,
          designation: data?.designation || data?.username || target,
          name: data?.username || data?.designation || target,
        },
      }),
    );
  };

  const triggerContactAction = useCallback(
    (actionType, data) => {
      const target = String(data?.extensionNumber || "").trim();

      if (!target) {
        return;
      }

      const contactPayload = {
        id: data?.uuid || data?.id || data?.extensionNumber,
        offEXtn: target,
        extension: target,
        designation: data?.designation || data?.name || data?.username,
        name: data?.name || data?.designation || data?.username,
      };

      if (typeof onContactAction === "function") {
        onContactAction(actionType, contactPayload);
        return;
      }

      if (actionType === "call") {
        onClickToCall(data);
        return;
      }

      if (actionType === "answer") {
        onClickToCall(data);
        return;
      }

      if (actionType === "video-call") {
        onClickToVideoCall(data);
        return;
      }

      if (actionType === "ambient-mode") {
        onClickToAmbientMode(data);
        return;
      }

      if (actionType === "message") {
        onClickToMessage(data);
      }
    },
    [onContactAction],
  );

  const onClearSOS = async (data) => {
    const target = data?.extensionNumber;
    if (!target) {
      return;
    }

    try {
      await telemetry.post(`/geoTagging/clearEmergency/${target}`);
      setNotify({
        isOpen: true,
        message: "",
        type: "success",
        pagename: "SOS Alert",
        status: "SOS Clear successfully",
      });
      setRefreshTick((previous) => previous + 1);
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: "SOS Alert",
        status:
          error?.response?.data?.messageDetail || "Failed to clear emergency",
      });
    }
  };

  const onClearALlSOS = () => {
    setOpenClearAllSOSDialog(true);
  };

  const onConfirmClearAllSOS = async () => {
    setOpenClearAllSOSDialog(false);
    try {
      await telemetry.post(`/geoTagging/clearAllEmergencies`);
      setNotify({
        isOpen: true,
        message: "",
        type: "success",
        pagename: "SOS Alert",
        status: "All SOS Cleared successfully",
      });
      setRefreshTick((previous) => previous + 1);
    } catch (error) {
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: "SOS Alert",
        status:
          error?.response?.data?.messageDetail || "Failed to clear emergencies",
      });
    }
  };

  const onCancelClearAllSOS = () => {
    setOpenClearAllSOSDialog(false);
  };

  const canGoPrevious = currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;
  const panelGradient = darkMode
    ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
    : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)";

  return (
    <>
      <Card
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          boxShadow: (theme) =>
            darkMode
              ? `0 4px 24px ${alpha(theme.palette.common.black, 0.4)}`
              : `0 2px 16px ${alpha(theme.palette.error.main, 0.07)}`,
          p: 0.9,
          gap: 0.7,
          background: (theme) =>
            darkMode
              ? `linear-gradient(160deg, ${alpha(theme.palette.background.default, 0.98)} 0%, ${alpha(theme.palette.error.dark, 0.06)} 100%)`
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.error.light, 0.04)} 100%)`,
        }}
      >
        {/* Single toolbar row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            flexWrap: "nowrap",
          }}
        >
          <Chip
            label={`Total Records: ${totalRecords}`}
            size="small"
            color="error"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.58rem", flexShrink: 0, "& .MuiChip-label": { px: 0.8 } }}
          />

          <MDTypography
            variant="caption"
            sx={{ fontSize: "0.63rem", fontWeight: 600, opacity: 0.7, flexShrink: 0 }}
          >
            Sort By
          </MDTypography>

          <Select
            size="small"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            sx={{
              flex: 1,
              maxWidth: 100,
              height: 26,
              fontSize: 11,
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => alpha(theme.palette.error.main, 0.3),
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                height: 26,
                py: 0,
                pr: 2.5,
                pl: 0.8,
              },
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Tooltip title={sortDirection === "desc" ? "Descending" : "Ascending"}>
            <IconButton
              size="small"
              onClick={() =>
                setSortDirection((previous) => previous === "desc" ? "asc" : "desc")
              }
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                borderRadius: 1.5,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                  transform: "scale(1.1)",
                },
              }}
            >
              {sortDirection === "desc" ? (
                <ArrowDownwardOutlinedIcon sx={{ fontSize: 13 }} color="error" />
              ) : (
                <ArrowUpwardOutlinedIcon sx={{ fontSize: 13 }} color="error" />
              )}
            </IconButton>
          </Tooltip>

          <MDTypography variant="caption" color="text" sx={{ opacity: 0.5, fontSize: "0.58rem", flexShrink: 0 }}>
            Auto Refresh: {hasActiveSOS ? "3s" : "5s"}
          </MDTypography>

          <MDButton
            variant="outlined"
            color="error"
            size="small"
            onClick={onClearALlSOS}
            sx={{
              minWidth: 0,
              height: 24,
              fontSize: "0.6rem",
              px: 0.9,
              py: 0,
              flexShrink: 0,
              borderRadius: 1.5,
              borderColor: (theme) => alpha(theme.palette.error.main, 0.4),
              color: (theme) => alpha(theme.palette.error.main, 0.85),
              textTransform: "none",
              transition: "all 0.18s",
              "&:hover": {
                borderColor: (theme) => alpha(theme.palette.error.main, 0.7),
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                transform: "scale(1.04)",
              },
            }}
          >
            Clear All
          </MDButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pr: 0.2,
            display: "grid",
            gap: 0.65,
            gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
            alignContent: "start",
          }}
        >
          {loading && rows.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 100,
                gap: 1,
                gridColumn: "1 / -1",
              }}
            >
              <CircularProgress size={22} thickness={4} color="error" />
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.5, fontSize: "0.6rem" }}>
                Loading alerts…
              </MDTypography>
            </Box>
          )}

          {!loading && rows.length === 0 && (
            <Card
              sx={{
                p: 1.5,
                border: (theme) => `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
                boxShadow: "none",
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1.5,
                background: "transparent",
              }}
            >
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                No emergency data found.
              </MDTypography>
            </Card>
          )}

          {rows.map((item) => {
            const identifier = item?.uuid || item?.id || item?.extensionNumber;
            const extensionNumber = String(item?.extensionNumber || "");
            const matchingCall = sosCallList.find(
              (callItem) =>
                String(callItem?.sender || "") === extensionNumber ||
                String(callItem?.streamId || "") === extensionNumber,
            );
            const isSOSWaiting = Boolean(matchingCall);
            const isSOSActive = item?.callStatus === "Active";

            return (
              <Card
                key={identifier}
                sx={{
                  p: 0.8,
                  minHeight: 68,
                  borderRadius: 1.5,
                  borderLeft: (theme) => `3px solid ${theme.palette.error.main}`,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                  boxShadow: (theme) =>
                    `0 0 10px ${alpha(theme.palette.error.main, 0.18)}, inset 0 0 8px ${alpha(theme.palette.error.main, 0.04)}`,
                  background: (theme) =>
                    darkMode
                      ? `linear-gradient(135deg, ${alpha(theme.palette.error.dark, 0.16)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.error.light, 0.07)} 0%, ${theme.palette.background.paper} 100%)`,
                  transition: "box-shadow 0.22s, transform 0.18s",
                  "&:hover": {
                    boxShadow: (theme) =>
                      `0 0 18px ${alpha(theme.palette.error.main, 0.32)}`,
                    transform: "translateY(-1px)",
                  },
                  "@keyframes sosPulse": {
                    "0%, 100%": { borderLeftColor: "error.main" },
                    "50%": { borderLeftColor: "transparent" },
                  },
                  animation: "sosPulse 1.4s ease-in-out infinite",
                }}
              >
                {/* Row 1: [30% icon+name] [70% chip+dept+battery] */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 0.35,
                    gap: 0.5,
                  }}
                >
                  {/* Left 30%: icon + name */}
                  <Box
                    sx={{
                      width: "30%",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.4,
                      minWidth: 0,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: (theme) => `1.5px solid ${alpha(theme.palette.error.main, 0.55)}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        background: (theme) => alpha(theme.palette.error.main, 0.1),
                      }}
                    >
                      <PersonOutlineIcon sx={{ fontSize: 13, color: "error.main" }} />
                    </Box>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{
                        lineHeight: 1.2,
                        fontSize: "0.62rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {emptyFallback(item?.designation || item?.username)}
                    </MDTypography>
                  </Box>

                  {/* Right 70%: chip + department + battery */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minWidth: 0,
                    }}
                  >
                    <Chip
                      label={item.emergencyStatus === "ManFallDown" ?  "ManFallDown"  : isSOSWaiting ? "SOS Waiting" : "SOS"}
                      size="small"
                      color={isSOSWaiting ? "warning" : isSOSActive ? "success" : "error"}
                      style={{
                        backgroundColor: isSOSWaiting
                          ? theme.palette.warning.main
                          : isSOSActive
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        color: "#fff",
                      }}
                      sx={{
                        height: 14,
                        fontSize: "0.5rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        flexShrink: 0,
                        "& .MuiChip-label": { px: 0.6 },
                        ...((isSOSActive || isSOSWaiting) && {
                          "@keyframes sosActivePulse": {
                            "0%, 100%": { transform: "scale(1)", opacity: 1 },
                            "50%": { transform: "scale(1.25)", opacity: 0.75 },
                          },
                          animation: "sosActivePulse 1.2s ease-in-out infinite",
                        }),
                      }}
                    />

                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.58rem", opacity: 0.72, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {emptyFallback(item?.department)}
                    </MDTypography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        "& .MuiTypography-root": { display: "inline-flex", alignItems: "center", gap: 0.2, fontSize: "0.52rem", lineHeight: 1, whiteSpace: "nowrap" },
                        "& .MuiIconButton-root": { p: 0, mr: 0.1 },
                        "& .MuiSvgIcon-root": { fontSize: 16 },
                      }}
                    >
                      <CTLBatteryStatus value={item?.batteryPercentage} />
                    </Box>
                  </Box>
                </Box>

                {/* Divider */}
                <Box
                  sx={{
                    height: "1px",
                    mx: -0.8,
                    mb: 0.4,
                    background: (theme) =>
                      `linear-gradient(90deg, transparent, ${alpha(theme.palette.error.main, 0.25)}, transparent)`,
                  }}
                />

                {/* Row 2: Time + Actions */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ fontSize: "0.59rem", opacity: 0.7, lineHeight: 1.3 }}
                  >
                    {emptyFallback(item?.emergencyCreatedOn, "Time unavailable")}
                  </MDTypography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexShrink: 0 }}>
                    {item?.callStatus === "Active" ? (() => {
                      const canAnswer = !!extensionNumber && !!matchingCall;
                      return (
                      <>
                        {/* {matchingCall && (
                          <Tooltip title="Answer Call">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => triggerContactAction("answer", item)}
                                disabled={!canAnswer}
                                sx={{
                                  width: 26,
                                  height: 26,
                                  borderRadius: "50%",
                                  border: (theme) => `1px solid ${alpha(theme.palette.success.main, canAnswer ? 0.4 : 0.15)}`,
                                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
                                  color: "success.main",
                                  transition: "all 0.18s",
                                  "&:not(:disabled):hover": {
                                    backgroundColor: (theme) => alpha(theme.palette.success.main, 0.25),
                                    transform: "scale(1.12)",
                                    boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.success.main, 0.35)}`,
                                  },
                                }}
                              >
                                <PhoneCallbackOutlinedIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )} */}
                        <Tooltip title="Disconnect Call">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => triggerContactAction("disconnect", item)}
                              disabled={!extensionNumber}
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
                                color: "error.main",
                                transition: "all 0.18s",
                                "&:not(:disabled):hover": {
                                  backgroundColor: (theme) => alpha(theme.palette.error.main, 0.25),
                                  transform: "scale(1.12)",
                                  boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.error.main, 0.35)}`,
                                },
                              }}
                            >
                              <CallEndOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                      );
                    })() : (
                      [
                        { title: "Audio Call", onClick: () => triggerContactAction("call", item), icon: <CallOutlinedIcon sx={{ fontSize: 13 }} />, color: "success", disabled: !item?.extensionNumber },
                        { title: "Video Call", onClick: () => triggerContactAction("video-call", item), icon: <VideocamOutlinedIcon sx={{ fontSize: 13 }} />, color: "info", disabled: !item?.extensionNumber },
                        { title: "Ambient Mode", onClick: () => triggerContactAction("ambient-mode", item), icon: <HearingOutlinedIcon sx={{ fontSize: 13 }} />, color: "error", disabled: !item?.extensionNumber },
                        { title: "Message", onClick: () => triggerContactAction("message", item), icon: <ChatOutlinedIcon sx={{ fontSize: 13 }} />, color: "warning", disabled: !item?.extensionNumber },
                        { title:  item.emergencyStatus === "ManFallDown"?"Clear Man Fall Down" :"Clear SOS", onClick: () => onClearSOS(item), icon: <DeleteForeverIcon sx={{ fontSize: 13 }} />, color: "error", disabled: !item?.extensionNumber },
                      ].map(({ title, onClick, icon, color, disabled }) => (
                        <Tooltip title={title} key={title}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={onClick}
                              disabled={disabled}
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                border: (theme) => `1px solid ${alpha(theme.palette[color].main, disabled ? 0.2 : 0.4)}`,
                                backgroundColor: (theme) => alpha(theme.palette[color].main, 0.1),
                                color: `${color}.main`,
                                transition: "all 0.18s",
                                "&:not(:disabled):hover": {
                                  backgroundColor: (theme) => alpha(theme.palette[color].main, 0.22),
                                  transform: "scale(1.12)",
                                  boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette[color].main, 0.35)}`,
                                },
                              }}
                            >
                              {icon}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ))
                    )}
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            pt: 0.2,
            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: "0.6rem" }}>
            Page {totalPages === 0 ? 0 : currentPage + 1} / {totalPages}
          </MDTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <IconButton
              size="small"
              color="text"
              disabled={!canGoPrevious}
              onClick={() =>
                setCurrentPage((previous) => Math.max(previous - 1, 0))
              }
              sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.error.main, canGoPrevious ? 0.4 : 0.15)}`,
                  transition: "all 0.18s",
                  "&:not(:disabled):hover": {
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                    transform: "scale(1.1)",
                  },
              }}
            >
                <NavigateBeforeOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <IconButton
              size="small"
              color="text"
              disabled={!canGoNext}
              onClick={() =>
                setCurrentPage((previous) =>
                  totalPages > 0
                    ? Math.min(previous + 1, totalPages - 1)
                    : previous,
                )
              }
              sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.error.main, canGoNext ? 0.4 : 0.15)}`,
                  transition: "all 0.18s",
                  "&:not(:disabled):hover": {
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                    transform: "scale(1.1)",
                  },
              }}
            >
                <NavigateNextOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <CTLAdvancedFilter
        entityName="Alarm View"
        columns={getColumns(onClickToCall, onClickToVideoCall, onClearSOS)}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
        advancedFilters={advancedFilters}
      />

      <CTLNotification notify={notify} setNotify={setNotify} />

      <Dialog
        open={openClearAllSOSDialog}
        onClose={onCancelClearAllSOS}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
        PaperProps={{
          sx: {
            pt: 2,
            backgroundColor: (theme) =>
              darkMode
                ? theme.palette.background.default
                : theme.palette.background.paper,
            border: (theme) =>
              darkMode
                ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
                : `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <MDBox display="flex" justifyContent="center">
          <MDBox
            sx={{
              p: 2,
              bgcolor: red[100],
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <WarningIcon sx={{ color: red[600], fontSize: 28 }} />
          </MDBox>
        </MDBox>

        <DialogTitle  sx={(theme) => ({
          ...CTLDialogContent(theme, { darkMode }),
          backgroundColor: darkMode
            ? theme.palette.background.default
            : theme.palette.background.paper,
          textAlign: "center",
          mb: 1,
        })}>
          <Typography variant="h6" fontWeight={600} color="text">
            Clear All SOS?
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={(theme) => ({
            ...CTLDialogContent(theme, { darkMode }),
            backgroundColor: darkMode
              ? theme.palette.background.default
              : theme.palette.background.paper,
            mt: 1,
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          })}
        >
          <MDTypography variant="body2" color="text" textAlign="center">
            Are you sure you want to clear all active SOS alerts?
            <br />
            This action cannot be undone.
          </MDTypography>
        </DialogContent>

        <DialogActions
          sx={(theme) => ({
            ...CTLDialogContent(theme, { darkMode }),
            backgroundColor: darkMode
              ? theme.palette.background.default
              : theme.palette.background.paper,
            mt: 3,
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
            px: 0,
          })}
        >
          <MDButton
            onClick={onCancelClearAllSOS}
            variant="outlined"
            color="text"
            size="small"
            sx={{
              width: { xs: "100%", md: 100 },
              height: 32,
              fontWeight: 500,
              fontSize: 12,
              textTransform: "none",
              borderColor: "#d1d5db",
              "&:hover": {
                backgroundColor: "#9d9d9e",
                color: "#111827",
                borderColor: "#d1d5db",
              },
            }}
          >
            Cancel
          </MDButton>

          <MDButton
            onClick={onConfirmClearAllSOS}
            variant="outlined"
            color="error"
            size="small"
            sx={{
              width: { xs: "100%", md: 100 },
              height: 32,
              fontWeight: 500,
              fontSize: 12,
              textTransform: "none",
              "&:hover": {
                backgroundColor: red[700],
                color: "#fff",
                borderColor: red[700],
              },
            }}
          >
            Clear All
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
